/**
 * EmptyKnowledgeBaseModal
 *
 * P2.4 — Blocking pre-flight modal that fires when a user starts an analysis
 * with an under-populated knowledge base (< MIN_DOCS).
 *
 * Why: drafts authored against an empty / sparse KB read as generic and
 * fabricate company-specific claims. Surfacing this *before* the user pays
 * for an analysis (and reads disappointing output) is the highest-leverage
 * intervention. The user can still proceed if they choose — the modal is
 * informational, not authoritative.
 *
 * Reads stats from KnowledgeBaseApi.getStats(). Renders a "Set up KB now"
 * primary action linking to /knowledge-base, plus a secondary "Continue
 * anyway — generic drafts" fallback.
 */

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, X, BookOpen } from 'lucide-react';
import { theme } from '../styles/theme';

const MIN_DOCS_RECOMMENDED = 3;

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, ${({ $open }) => ($open ? '0.55' : '0')});
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: background 0.2s ease;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const Card = styled.div<{ $open: boolean }>`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  width: 100%;
  max-width: 540px;
  transform: scale(${({ $open }) => ($open ? '1' : '0.96')});
  opacity: ${({ $open }) => ($open ? '1' : '0')};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[3]};
  background: linear-gradient(135deg, ${theme.colors.warning[100]}, ${theme.colors.warning[100]}40);
  border-bottom: 1px solid ${theme.colors.warning[100]};
`;

const TitleBlock = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  flex: 1;
  min-width: 0;
  h3 {
    margin: 0 0 ${theme.spacing[1]};
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
  }
  p {
    margin: 0;
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
    line-height: ${theme.typography.lineHeight.relaxed};
  }
`;

const IconHolder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.warning[100]};
  color: ${theme.colors.warning[700]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: ${theme.spacing[1]};
  border-radius: ${theme.borderRadius.sm};
  &:hover {
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }
`;

const Body = styled.div`
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.md};
`;

const StatItem = styled.div`
  flex: 1;
  .value {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    font-variant-numeric: tabular-nums;
  }
  .label {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-top: 2px;
  }
`;

const ConsequenceList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
  line-height: ${theme.typography.lineHeight.relaxed};

  li {
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacing[2]};
    &::before {
      content: '•';
      color: ${theme.colors.warning[600]};
      font-weight: ${theme.typography.fontWeight.bold};
      flex-shrink: 0;
      line-height: 1.4;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: ${theme.colors.backgroundSecondary};
  border-top: 1px solid ${theme.colors.border};
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[600]};
  color: white;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  text-decoration: none;
  transition: all ${theme.transitions.base};
  &:hover {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textSecondary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  &:hover {
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }
`;

interface Props {
  open: boolean;
  /** Number of KB documents currently uploaded. */
  documentCount: number;
  /** Number of categories the user has at least one document in. */
  categoriesWithDocuments?: number;
  /** Critical missing categories returned by /knowledge-base/setup-progress. */
  missingCriticalCategories?: string[];
  onProceedAnyway: () => void;
  onCancel: () => void;
}

const EmptyKnowledgeBaseModal: React.FC<Props> = ({
  open,
  documentCount,
  categoriesWithDocuments,
  missingCriticalCategories,
  onProceedAnyway,
  onCancel,
}) => {
  if (!open) return null;

  const isEmpty = documentCount === 0;
  const title = isEmpty
    ? 'Your knowledge base is empty'
    : 'Your knowledge base is sparse';
  const lead = isEmpty
    ? "We can still extract questions and run an evaluation, but any auto-drafted responses will read as generic — they won't reference your case studies, certifications, or past performance."
    : `You have only ${documentCount} document${documentCount === 1 ? '' : 's'} in your KB. We recommend at least ${MIN_DOCS_RECOMMENDED} across capability, past performance, and compliance categories so the AI can ground drafts in your evidence.`;

  return (
    <Overlay $open={open} role="dialog" aria-modal="true">
      <Card $open={open}>
        <Header>
          <TitleBlock>
            <IconHolder>
              <AlertTriangle size={22} />
            </IconHolder>
            <div>
              <h3>{title}</h3>
              <p>{lead}</p>
            </div>
          </TitleBlock>
          <CloseBtn onClick={onCancel} aria-label="Close">
            <X size={18} />
          </CloseBtn>
        </Header>

        <Body>
          <StatsRow>
            <StatItem>
              <div className="value">{documentCount}</div>
              <div className="label">Documents</div>
            </StatItem>
            <StatItem>
              <div className="value">{categoriesWithDocuments ?? 0}</div>
              <div className="label">Categories filled</div>
            </StatItem>
            <StatItem>
              <div className="value">{missingCriticalCategories?.length ?? 0}</div>
              <div className="label">Critical gaps</div>
            </StatItem>
          </StatsRow>

          <div>
            <p
              style={{
                margin: `0 0 ${theme.spacing[2]}`,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.textPrimary,
              }}
            >
              What this means for your bid:
            </p>
            <ConsequenceList>
              <li>AI-drafted answers will lack named projects, awards, and certifications.</li>
              <li>The KB-coverage assessment on every drafted question will read "poor" or "critical".</li>
              <li>Refinements of inline responses still work, but won't be enriched with your evidence.</li>
            </ConsequenceList>
          </div>

          {missingCriticalCategories && missingCriticalCategories.length > 0 && (
            <div
              style={{
                background: theme.colors.warning[100] + '60',
                border: `1px solid ${theme.colors.warning[100]}`,
                borderRadius: theme.borderRadius.md,
                padding: theme.spacing[3],
              }}
            >
              <p
                style={{
                  margin: `0 0 ${theme.spacing[2]}`,
                  fontSize: theme.typography.fontSize.xs,
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.warning[700],
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Recommended categories still missing
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textPrimary,
                  lineHeight: theme.typography.lineHeight.relaxed,
                }}
              >
                {missingCriticalCategories.slice(0, 6).join(' · ')}
                {missingCriticalCategories.length > 6 ? ` (+${missingCriticalCategories.length - 6} more)` : ''}
              </p>
            </div>
          )}
        </Body>

        <Actions>
          <SecondaryButton onClick={onProceedAnyway}>
            Continue anyway
          </SecondaryButton>
          <PrimaryButton to="/knowledge-base" onClick={onCancel}>
            <BookOpen size={16} />
            Set up KB now
            <ArrowRight size={16} />
          </PrimaryButton>
        </Actions>
      </Card>
    </Overlay>
  );
};

export default EmptyKnowledgeBaseModal;
export { MIN_DOCS_RECOMMENDED };
